import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonBackButton,
  IonButtons,
  IonSpinner,
  IonToast,
  IonIcon,
} from '@ionic/react';
import {
  checkmarkCircle,
  closeCircle,
  time,
  shieldCheckmark,
  refresh,
  arrowBack,
  create,
} from 'ionicons/icons';
import { useRequestVerification, useLatestVerification, useMyCandidateProfile, useCandidateProfile, useLegalEntity } from '@/lib/api/hooks';
import { NoindexSeo } from '@/components/NoindexSeo';
import { AppFooter } from '@/components/AppFooter';
import './VerifyIdentity.css';

const STATUS_CONFIG = {
  Verified: { icon: checkmarkCircle, heroClass: 'vi-hero--verified', label: 'Identity Verified' },
  Failed: { icon: closeCircle, heroClass: 'vi-hero--failed', label: 'Verification Failed' },
  ManualReview: { icon: time, heroClass: 'vi-hero--review', label: 'Under Review' },
  Pending: { icon: time, heroClass: 'vi-hero--pending', label: 'Pending Review' },
} as const;

export function VerifyIdentityPage() {
  const history = useHistory();
  // Candidate profile id via /candidates/me — authStore profile.id is the userprofile id, not candidateprofile id.
  const { data: myCandidateEnvelope } = useMyCandidateProfile();
  const candidateId = myCandidateEnvelope?.data?._id ?? '';

  const { data: verificationEnvelope, isLoading: loadingVerification } = useLatestVerification(candidateId);
  const verification = verificationEnvelope?.data;

  const requestVerification = useRequestVerification();

  // Pre-populate from LegalEntity nationalIds
  const { data: candidateProfileEnvelope } = useCandidateProfile(candidateId);
  const legalEntityId = candidateProfileEnvelope?.data?.legalEntity?.id ?? '';
  const { data: legalEntityEnvelope } = useLegalEntity(legalEntityId);
  const nationalIds = legalEntityEnvelope?.data?.nationalIds ?? [];

  const [idNumber, setIdNumber] = useState('');
  const [fullName, setFullName] = useState('');
  const [idType, setIdType] = useState<'SA_ID' | 'PASSPORT'>('SA_ID');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ msg: string; color: string } | null>(null);
  const [prePopulated, setPrePopulated] = useState(false);

  // Pre-populate from nationalIds once data loads
  useEffect(() => {
    if (prePopulated || nationalIds.length === 0) return;
    const primary = nationalIds[0];
    if (primary?.number) {
      setIdNumber(primary.number);
      if (primary.type === 'Passport') setIdType('PASSPORT');
    }
    setPrePopulated(true);
  }, [nationalIds, prePopulated]);

  const statusInfo = verification ? STATUS_CONFIG[verification.status] : null;

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {};
    if (!fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!idNumber.trim()) newErrors.idNumber = 'ID number is required';
    else if (idType === 'SA_ID' && !/^\d{13}$/.test(idNumber)) newErrors.idNumber = 'SA ID must be 13 digits';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    try {
      await requestVerification.mutateAsync({
        candidateId,
        idNumber,
        fullName,
        idType,
      });
      setToast({ msg: 'Verification submitted', color: 'success' });
    } catch {
      setToast({ msg: 'Failed to submit verification', color: 'danger' });
    }
  };

  return (
    <IonPage>
      <NoindexSeo />
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/profile" />
          </IonButtons>
          <IonTitle>Verify Identity</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {loadingVerification ? (
          <div className="vi-page">
            <div className="vi-loading">
              <IonSpinner name="crescent" />
            </div>
          </div>
        ) : verification && statusInfo ? (
          <div className="vi-page">
            {/* ── Status hero ── */}
            <div className={`vi-hero ${statusInfo.heroClass}`}>
              <div className="vi-hero-glow" />
              <div className="vi-hero-icon">
                <IonIcon icon={statusInfo.icon} />
              </div>
              <h2>{statusInfo.label}</h2>
              <div className="vi-detail-pill">
                <IonIcon icon={shieldCheckmark} />
                <span>{verification.provider}</span>
              </div>
              {verification.result.evidence && (
                <div className="vi-evidence" style={{ marginTop: '16px' }}>
                  <div className="vi-evidence-label">Details</div>
                  <p className="vi-evidence-text">{verification.result.evidence}</p>
                </div>
              )}
            </div>

            {/* ── Actions ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '20px' }}>
              {verification.status === 'Verified' ? (
                <button className="vi-submit" onClick={() => history.goBack()}>
                  <IonIcon icon={checkmarkCircle} />
                  Done
                </button>
              ) : verification.status === 'Failed' ? (
                <>
                  <button className="vi-submit" onClick={() => history.push('/profile/verify-identity')}>
                    <IonIcon icon={refresh} />
                    Try again
                  </button>
                  <button className="vi-back" onClick={() => history.goBack()}>
                    <IonIcon icon={arrowBack} />
                    Back
                  </button>
                </>
              ) : (
                <button className="vi-back" onClick={() => history.goBack()}>
                  <IonIcon icon={arrowBack} />
                  Back
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="vi-page">
            {/* ── Request hero ── */}
            <div className="vi-hero">
              <div className="vi-hero-glow" />
              <div className="vi-hero-icon">
                <IonIcon icon={shieldCheckmark} />
              </div>
              <h2>Verify your identity</h2>
              <p>Confirm your South African ID to earn a verified badge on your profile.</p>
            </div>

            {/* ── Form panel ── */}
            <div className="vi-form-panel" style={{ marginTop: '20px' }}>
              <div className="vi-form-head">
                <IonIcon icon={create} />
                <h3>Enter your details</h3>
              </div>

              <div className="vi-form-body">
                {/* Full name */}
                <div className="vi-field">
                  <label className="vi-label">Full name (as on ID)</label>
                  <input
                    className="vi-input"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="e.g. John Doe"
                  />
                  {errors.fullName && <span className="vi-error">{errors.fullName}</span>}
                </div>

                {/* ID type */}
                <div className="vi-field">
                  <label className="vi-label">ID type</label>
                  <select
                    className="vi-select"
                    value={idType}
                    onChange={(e) => setIdType(e.target.value as 'SA_ID' | 'PASSPORT')}
                  >
                    <option value="SA_ID">South African ID</option>
                    <option value="PASSPORT">Passport</option>
                  </select>
                </div>

                {/* ID number */}
                <div className="vi-field">
                  <label className="vi-label">
                    {idType === 'SA_ID' ? 'SA ID number' : 'Passport number'}
                  </label>
                  <input
                    className="vi-input"
                    type="text"
                    value={idNumber}
                    onChange={(e) => setIdNumber(e.target.value)}
                    placeholder={idType === 'SA_ID' ? '0000000000000' : 'Passport number'}
                    maxLength={idType === 'SA_ID' ? 13 : 20}
                  />
                  {errors.idNumber && <span className="vi-error">{errors.idNumber}</span>}
                </div>

                {/* Submit */}
                <button
                  className="vi-submit"
                  onClick={handleSubmit}
                  disabled={requestVerification.isPending}
                >
                  {requestVerification.isPending ? (
                    <IonSpinner name="crescent" />
                  ) : (
                    <>
                      <IonIcon icon={shieldCheckmark} />
                      Submit verification
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <AppFooter />
      </IonContent>

      <IonToast isOpen={!!toast} message={toast?.msg} color={toast?.color} duration={2200} onDidDismiss={() => setToast(null)} />
    </IonPage>
  );
}
